import React, { useEffect, useMemo, useState } from "react";

import {
    CheckSquareOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    UserOutlined
} from "@ant-design/icons";

import {
    canSyncRehabData,
    getActiveRehabProfileId,
    getRehabHistory,
    saveRehabData,
    deleteRehabData
} from "../../api/rehabTracking";

import {
    getPatientProfiles
} from "../../api/patientProfile";


export default function Barthel() {

    const initialAnswers = {
        feeding: "",
        bathing: "",
        grooming: "",
        dressing: "",
        bowels: "",
        bladder: "",
        toilet: "",
        transfer: "",
        mobility: "",
        stairs: ""
    };


    const [answers, setAnswers] =
        useState(initialAnswers);

    const [note, setNote] =
        useState("");

    const [history, setHistory] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [message, setMessage] =
        useState("");

    const [activeProfile, setActiveProfile] =
        useState(null);


    /*
     * CÁC MỤC BARTHEL
     */
    const questions = [

        {
            key: "feeding",
            title: "Ăn uống",
            options: [
                {
                    value: 0,
                    label: "Phụ thuộc hoàn toàn"
                },
                {
                    value: 5,
                    label: "Cần trợ giúp"
                },
                {
                    value: 10,
                    label: "Tự thực hiện"
                }
            ]
        },

        {
            key: "bathing",
            title: "Tắm",
            options: [
                {
                    value: 0,
                    label: "Cần trợ giúp"
                },
                {
                    value: 5,
                    label: "Tự thực hiện"
                }
            ]
        },

        {
            key: "grooming",
            title: "Vệ sinh cá nhân",
            description:
                "Ví dụ: rửa mặt, chải tóc, đánh răng...",
            options: [
                {
                    value: 0,
                    label: "Cần trợ giúp"
                },
                {
                    value: 5,
                    label: "Tự thực hiện"
                }
            ]
        },

        {
            key: "dressing",
            title: "Mặc quần áo",
            options: [
                {
                    value: 0,
                    label: "Phụ thuộc"
                },
                {
                    value: 5,
                    label: "Cần trợ giúp"
                },
                {
                    value: 10,
                    label: "Tự thực hiện"
                }
            ]
        },

        {
            key: "bowels",
            title: "Kiểm soát đại tiện",
            options: [
                {
                    value: 0,
                    label: "Không kiểm soát"
                },
                {
                    value: 5,
                    label: "Thỉnh thoảng có sự cố"
                },
                {
                    value: 10,
                    label: "Kiểm soát"
                }
            ]
        },

        {
            key: "bladder",
            title: "Kiểm soát tiểu tiện",
            options: [
                {
                    value: 0,
                    label: "Không kiểm soát"
                },
                {
                    value: 5,
                    label: "Thỉnh thoảng có sự cố"
                },
                {
                    value: 10,
                    label: "Kiểm soát"
                }
            ]
        },

        {
            key: "toilet",
            title: "Sử dụng nhà vệ sinh",
            options: [
                {
                    value: 0,
                    label: "Phụ thuộc"
                },
                {
                    value: 5,
                    label: "Cần một phần trợ giúp"
                },
                {
                    value: 10,
                    label: "Tự thực hiện"
                }
            ]
        },

        {
            key: "transfer",
            title: "Di chuyển giường - ghế",
            options: [
                {
                    value: 0,
                    label: "Không thực hiện được"
                },
                {
                    value: 5,
                    label: "Cần trợ giúp nhiều"
                },
                {
                    value: 10,
                    label: "Cần trợ giúp ít"
                },
                {
                    value: 15,
                    label: "Tự thực hiện"
                }
            ]
        },

        {
            key: "mobility",
            title: "Di chuyển trên mặt phẳng",
            options: [
                {
                    value: 0,
                    label: "Không di chuyển được"
                },
                {
                    value: 5,
                    label: "Tự di chuyển bằng xe lăn"
                },
                {
                    value: 10,
                    label: "Đi lại với trợ giúp"
                },
                {
                    value: 15,
                    label: "Đi lại độc lập"
                }
            ]
        },

        {
            key: "stairs",
            title: "Lên xuống cầu thang",
            options: [
                {
                    value: 0,
                    label: "Không thực hiện được"
                },
                {
                    value: 5,
                    label: "Cần trợ giúp"
                },
                {
                    value: 10,
                    label: "Tự thực hiện"
                }
            ]
        }
    ];


    /*
     * TỔNG ĐIỂM
     */
    const totalScore =
        useMemo(() => {

            return Object.values(
                answers
            ).reduce(
                (
                    total,
                    value
                ) => {

                    if (
                        value === "" ||
                        value === null ||
                        value === undefined
                    ) {

                        return total;
                    }


                    return (
                        total +
                        Number(value)
                    );
                },
                0
            );

        }, [answers]);


    /*
     * ĐÃ HOÀN THÀNH?
     */
    const completed =
        useMemo(() => {

            return Object.values(
                answers
            ).every(
                (value) =>
                    value !== "" &&
                    value !== null &&
                    value !== undefined
            );

        }, [answers]);


    /*
     * THAY ĐỔI CÂU TRẢ LỜI
     */
    const handleAnswerChange = (
        questionKey,
        value
    ) => {

        setAnswers(
            (prev) => ({
                ...prev,
                [questionKey]:
                    Number(value)
            })
        );
    };


    /*
     * FORMAT NGÀY GIỜ
     */
    const formatDateTime = (value) => {

        if (!value) {

            return {
                date: "",
                time: ""
            };
        }


        const dateObject =
            new Date(value);


        if (
            Number.isNaN(
                dateObject.getTime()
            )
        ) {

            return {
                date: "",
                time: ""
            };
        }


        return {

            date:
                dateObject.toLocaleDateString(
                    "vi-VN"
                ),

            time:
                dateObject.toLocaleTimeString(
                    "vi-VN",
                    {
                        hour: "2-digit",
                        minute: "2-digit"
                    }
                )
        };
    };


    /*
     * LOAD HỒ SƠ ĐANG CHỌN
     */
    const loadActiveProfile =
        async () => {

            try {

                if (!canSyncRehabData()) {

                    setActiveProfile(null);

                    return;
                }


                const activeId =
                    getActiveRehabProfileId();


                if (!activeId) {

                    setActiveProfile(null);

                    return;
                }


                const profiles =
                    await getPatientProfiles();


                const found =
                    Array.isArray(profiles)
                        ? profiles.find(
                            (item) =>
                                Number(
                                    item.profile_id
                                ) ===
                                Number(activeId)
                        )
                        : null;


                setActiveProfile(
                    found || null
                );

            } catch (error) {

                console.error(
                    "Không thể tải hồ sơ:",
                    error
                );


                setActiveProfile(null);
            }
        };


    /*
     * LOAD LỊCH SỬ
     */
    const loadHistory =
        async () => {

            setLoading(true);

            setMessage("");


            try {

                if (!canSyncRehabData()) {

                    setHistory([]);

                    setMessage(
                        "Vui lòng đăng nhập để lưu và xem dữ liệu phục hồi chức năng."
                    );

                    return;
                }


                if (!getActiveRehabProfileId()) {

                    setHistory([]);

                    setMessage(
                        "Vui lòng chọn hồ sơ đang theo dõi trước khi sử dụng công cụ."
                    );

                    return;
                }


                const data =
                    await getRehabHistory(
                        "barthel"
                    );


                const formatted =
                    Array.isArray(data)
                        ? data.map(
                            (item) => {

                                const tracking =
                                    item.tracking_data ||
                                    {};


                                const dateTime =
                                    formatDateTime(
                                        item.created_at
                                    );


                                return {

                                    id:
                                        item.health_tracking_id,

                                    score:
                                        Number(
                                            tracking.score ??
                                            0
                                        ),

                                    answers:
                                        tracking.answers ||
                                        {},

                                    note:
                                        tracking.note ||
                                        "",

                                    date:
                                        tracking.date ||
                                        dateTime.date,

                                    time:
                                        tracking.time ||
                                        dateTime.time
                                };
                            }
                        )
                        : [];


                setHistory(
                    formatted
                );

            } catch (error) {

                console.error(
                    "Không thể tải lịch sử Barthel:",
                    error
                );


                setHistory([]);


                setMessage(
                    error?.message ||
                    "Không thể tải lịch sử Barthel."
                );

            } finally {

                setLoading(false);
            }
        };


    /*
     * LOAD KHI MỞ TRANG
     */
    useEffect(() => {

        loadActiveProfile();

        loadHistory();

    }, []);


    /*
     * LƯU
     */
    const handleSave =
        async () => {

            if (!canSyncRehabData()) {

                alert(
                    "Vui lòng đăng nhập trước khi lưu dữ liệu."
                );

                return;
            }


            if (!getActiveRehabProfileId()) {

                alert(
                    "Vui lòng chọn hồ sơ đang theo dõi trước."
                );

                return;
            }


            if (!completed) {

                alert(
                    "Vui lòng hoàn thành tất cả các mục trước khi lưu."
                );

                return;
            }


            const now =
                new Date();


            const trackingData = {

                score:
                    totalScore,

                answers,

                note:
                    note.trim(),

                date:
                    now.toLocaleDateString(
                        "vi-VN"
                    ),

                time:
                    now.toLocaleTimeString(
                        "vi-VN",
                        {
                            hour: "2-digit",
                            minute: "2-digit"
                        }
                    )
            };


            setSaving(true);

            setMessage("");


            try {

                await saveRehabData(
                    "barthel",
                    trackingData
                );


                setAnswers({
                    ...initialAnswers
                });

                setNote("");


                await loadHistory();


                setMessage(
                    "Đã lưu kết quả Barthel."
                );

            } catch (error) {

                console.error(
                    "Không thể lưu Barthel:",
                    error
                );


                setMessage(
                    error?.message ||
                    "Không thể lưu kết quả Barthel."
                );

            } finally {

                setSaving(false);
            }
        };


    /*
     * XÓA
     */
    const handleDelete =
        async (id) => {

            const confirmed =
                window.confirm(
                    "Bạn có chắc muốn xóa kết quả Barthel này?"
                );


            if (!confirmed) {
                return;
            }


            try {

                await deleteRehabData(
                    id
                );


                setHistory(
                    (current) =>
                        current.filter(
                            (item) =>
                                Number(item.id) !==
                                Number(id)
                        )
                );


                setMessage(
                    "Đã xóa kết quả Barthel."
                );

            } catch (error) {

                console.error(
                    "Không thể xóa Barthel:",
                    error
                );


                setMessage(
                    error?.message ||
                    "Không thể xóa kết quả Barthel."
                );
            }
        };


    return (

        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">


            {/* QUAY LẠI */}

            <a
                href="/tienich/phuc-hoi-chuc-nang"
                className="inline-flex items-center text-emerald-600 font-semibold text-sm mb-4"
            >

                <ArrowLeftOutlined className="mr-2" />

                Phục hồi chức năng

            </a>


            {/* HEADER */}

            <div className="bg-gradient-to-r from-green-600 to-emerald-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">

                        <CheckSquareOutlined />

                    </div>


                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">

                            PHỤC HỒI CHỨC NĂNG

                        </p>


                        <h1 className="text-[25px] md:text-3xl font-bold">

                            Barthel Index

                        </h1>

                    </div>

                </div>


                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal max-w-3xl">

                    Ghi nhận mức độ độc lập trong một số hoạt động sinh hoạt hằng ngày.

                </p>

            </div>


            {/* HỒ SƠ ĐANG THEO DÕI */}

            {activeProfile ? (

                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mt-4">

                    <div className="flex items-center">

                        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">

                            <UserOutlined />

                        </div>


                        <div className="ml-3">

                            <p className="text-[11px] font-bold text-emerald-700 uppercase">

                                Đang ghi dữ liệu cho

                            </p>


                            <p className="font-bold text-gray-900">

                                {activeProfile.profile_name}

                            </p>


                            {activeProfile.relationship && (

                                <p className="text-xs text-gray-500 mt-0.5">

                                    {activeProfile.relationship}

                                </p>

                            )}

                        </div>

                    </div>

                </div>

            ) : (

                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-4">

                    <p className="font-semibold text-gray-800 text-sm">

                        Chưa chọn hồ sơ theo dõi

                    </p>


                    <p className="text-[13px] text-gray-600 mt-1">

                        Quay lại trang Phục hồi chức năng để chọn hoặc tạo hồ sơ trước khi lưu dữ liệu.

                    </p>


                    <a
                        href="/tienich/phuc-hoi-chuc-nang"
                        className="inline-block mt-2 text-emerald-600 font-semibold text-sm"
                    >

                        Chọn hồ sơ

                    </a>

                </div>

            )}


            {/* THÔNG BÁO */}

            {message && (

                <div className="bg-gray-50 border rounded-xl px-4 py-3 mt-4 text-[13px] text-gray-600">

                    {message}

                </div>

            )}


            {/* ĐIỂM HIỆN TẠI */}

            <div className="bg-white border rounded-2xl p-4 md:p-5 mt-5">

                <div className="flex items-center justify-between">

                    <div>

                        <p className="text-sm text-gray-500">

                            Tổng điểm hiện tại

                        </p>


                        <p className="text-[13px] text-gray-400 mt-1">

                            Hoàn thành các mục bên dưới để lưu kết quả.

                        </p>

                    </div>


                    <div className="bg-green-50 text-green-700 rounded-xl px-4 py-2 text-center">

                        <p className="text-2xl font-bold">

                            {totalScore}

                        </p>


                        <p className="text-[11px]">

                            / 100 điểm

                        </p>

                    </div>

                </div>

            </div>


            {/* CÁC MỤC ĐÁNH GIÁ */}

            <div className="mt-6">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Các mục đánh giá

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    Ghi nhận phương án phù hợp với kết quả đánh giá hiện tại.

                </p>


                <div className="space-y-4 mt-4">

                    {questions.map(
                        (
                            question,
                            questionIndex
                        ) => (

                            <div
                                key={question.key}
                                className="bg-white border rounded-2xl p-4 md:p-5"
                            >

                                <div className="flex items-start">

                                    <div className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold text-sm flex-shrink-0">

                                        {questionIndex + 1}

                                    </div>


                                    <div className="ml-3 flex-1">

                                        <p className="font-bold text-gray-800">

                                            {question.title}

                                        </p>


                                        {question.description && (

                                            <p className="text-[12px] text-gray-500 mt-1">

                                                {question.description}

                                            </p>

                                        )}

                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">

                                    {question.options.map(
                                        (option) => {

                                            const selected =
                                                answers[
                                                    question.key
                                                ] ===
                                                option.value;


                                            return (

                                                <button
                                                    key={
                                                        option.value
                                                    }
                                                    type="button"
                                                    onClick={() =>
                                                        handleAnswerChange(
                                                            question.key,
                                                            option.value
                                                        )
                                                    }
                                                    className={`w-full text-left border rounded-xl px-3 py-3 transition ${
                                                        selected
                                                            ? "border-green-500 bg-green-50"
                                                            : "border-gray-200 bg-white"
                                                    }`}
                                                >

                                                    <div className="flex items-center justify-between">

                                                        <span
                                                            className={`text-[13px] ${
                                                                selected
                                                                    ? "text-green-700 font-semibold"
                                                                    : "text-gray-700"
                                                            }`}
                                                        >

                                                            {option.label}

                                                        </span>


                                                        <span
                                                            className={`text-[12px] font-bold ml-3 ${
                                                                selected
                                                                    ? "text-green-600"
                                                                    : "text-gray-400"
                                                            }`}
                                                        >

                                                            {option.value} điểm

                                                        </span>

                                                    </div>

                                                </button>

                                            );
                                        }
                                    )}

                                </div>

                            </div>

                        )
                    )}

                </div>

            </div>


            {/* GHI CHÚ */}

            <div className="bg-white border rounded-2xl p-4 md:p-5 mt-5">

                <label className="block text-sm font-semibold text-gray-700 mb-2">

                    Ghi chú

                    <span className="font-normal text-gray-400 ml-1">

                        (không bắt buộc)

                    </span>

                </label>


                <textarea
                    value={note}
                    onChange={(e) =>
                        setNote(
                            e.target.value
                        )
                    }
                    placeholder="Nhập ghi chú cho lần đánh giá này..."
                    rows={3}
                    className="w-full border rounded-xl px-3 py-3 outline-none focus:border-green-500 resize-none"
                />


                <button
                    type="button"
                    onClick={
                        handleSave
                    }
                    disabled={
                        saving ||
                        !activeProfile
                    }
                    className="w-full mt-4 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >

                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu kết quả Barthel"
                    }

                </button>

            </div>


            {/* LỊCH SỬ */}

            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Lịch sử đánh giá

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    Các kết quả Barthel của hồ sơ đang được chọn.

                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">

                            Đang tải dữ liệu...

                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <CheckSquareOutlined className="text-3xl text-gray-300" />


                        <p className="font-semibold text-gray-700 mt-3">

                            Chưa có kết quả

                        </p>


                        <p className="text-sm text-gray-500 mt-1">

                            Kết quả Barthel của hồ sơ này sẽ xuất hiện tại đây.

                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map(
                            (item) => (

                                <div
                                    key={item.id}
                                    className="bg-white border rounded-2xl p-4"
                                >

                                    <div className="flex items-start justify-between">

                                        <div className="flex-1 pr-3">

                                            <div className="flex items-end">

                                                <p className="text-3xl font-bold text-green-600">

                                                    {item.score}

                                                </p>


                                                <span className="text-sm text-gray-400 ml-1 mb-1">

                                                    / 100 điểm

                                                </span>

                                            </div>


                                            <p className="text-xs text-gray-400 mt-2">

                                                {item.time} - {item.date}

                                            </p>


                                            {item.note && (

                                                <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3">

                                                    <p className="text-[13px] text-gray-500">

                                                        {item.note}

                                                    </p>

                                                </div>

                                            )}

                                        </div>


                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleDelete(
                                                    item.id
                                                )
                                            }
                                            className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"
                                            aria-label="Xóa kết quả Barthel"
                                        >

                                            <DeleteOutlined />

                                        </button>

                                    </div>

                                </div>

                            )
                        )}

                    </div>

                )}

            </div>


            {/* LƯU Ý */}

            <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">

                    Lưu ý

                </p>


                <p className="text-[13px] text-gray-600 leading-5 mt-1">

                    Barthel Index dùng để ghi nhận khả năng thực hiện một số hoạt động sinh hoạt hằng ngày.

                </p>


                <p className="text-[13px] text-gray-500 leading-5 mt-2">

                    Điểm số trên website hỗ trợ lưu và theo dõi kết quả,
                    không thay thế đánh giá lâm sàng của nhân viên y tế.

                </p>

            </div>

        </div>
    );
}